import type { TemplateResult } from "lit";
import { html } from "lit";
import { createDonateClickEvent, sanitizeHeaderHref } from "@ligonier/shared";

/**
 * Default donation host. `give.ligonier.org` (the previous hardcoded value)
 * does not resolve, so the footer points at the working donate host used
 * across the Ligonier sites. Consumers can override via the footer's
 * `donate-url` attribute for environment-specific donation links.
 */
export const DEFAULT_DONATE_URL = "https://donate.ligonier.org";

/**
 * Give CTAs dispatch a cancelable `donate-click` (mirroring the header's
 * donate button) before navigating, so hosts can take over the donate action.
 * Modified/middle clicks keep native anchor behavior and skip the event.
 */
const handleDonateClick =
  (intent: "monthly" | "once") =>
  (event: MouseEvent): void => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const anchor = event.currentTarget as HTMLElement;
    const proceed = anchor.dispatchEvent(
      createDonateClickEvent({ source: "footer-callout", intent })
    );
    if (!proceed) {
      event.preventDefault();
    }
  };

export function renderCalloutFooter(
  donateUrl: string = DEFAULT_DONATE_URL,
): TemplateResult {
  // Both CTAs point at the donation host. The old "/partner" suffix 404s on
  // donate.ligonier.org (it only existed on the retired give.ligonier.org); the
  // live site sends both "Give Monthly" and "One-Time Gift" to the same donate
  // host (the checkout widget handles the gift type), so we do the same here.
  const safeDonateUrl = sanitizeHeaderHref(donateUrl);
  return html`
    <div class="callout-footer">
      <div class="callout-image">
        <div class="callout-image-wrapper">
          <img
            width="200"
            height="280"
            data-main-image=""
            sizes="(min-width: 200px) 200px, 100vw"
            decoding="async"
            src="//images.ctfassets.net/ukqj0ybhazqg/7zvrpbD1GvubE5ZvgLr3ml/7f7c28afbae7f9faf370a87256e62b6a/International_RSB_Bibles__1_.jpg?w=200&amp;h=280&amp;fl=progressive&amp;q=100&amp;fm=jpg"
            srcset="
              //images.ctfassets.net/ukqj0ybhazqg/7zvrpbD1GvubE5ZvgLr3ml/7f7c28afbae7f9faf370a87256e62b6a/International_RSB_Bibles__1_.jpg?w=50&amp;h=70&amp;fl=progressive&amp;q=100&amp;fm=jpg    50w,
              //images.ctfassets.net/ukqj0ybhazqg/7zvrpbD1GvubE5ZvgLr3ml/7f7c28afbae7f9faf370a87256e62b6a/International_RSB_Bibles__1_.jpg?w=100&amp;h=140&amp;fl=progressive&amp;q=100&amp;fm=jpg 100w,
              //images.ctfassets.net/ukqj0ybhazqg/7zvrpbD1GvubE5ZvgLr3ml/7f7c28afbae7f9faf370a87256e62b6a/International_RSB_Bibles__1_.jpg?w=200&amp;h=280&amp;fl=progressive&amp;q=100&amp;fm=jpg 200w,
              //images.ctfassets.net/ukqj0ybhazqg/7zvrpbD1GvubE5ZvgLr3ml/7f7c28afbae7f9faf370a87256e62b6a/International_RSB_Bibles__1_.jpg?w=400&amp;h=560&amp;fl=progressive&amp;q=100&amp;fm=jpg 400w
            "
            alt=""
            style="object-fit: cover; opacity: 1;"
          />
        </div>
      </div>
      <div class="callout-content">
        <h2 class="callout-title">You Can Make a Difference</h2>
        <div class="callout-sections">
          <section class="callout-section">
            <img
              class="callout-section-icon"
              src="//images.ctfassets.net/ukqj0ybhazqg/3vUbXl8E2GTWXLLvwWCA2p/1b0299663d7ddfd0ef1858ed3209f2ec/24px-donate.svg"
              alt=""
            />
            <div class="callout-section-content">
              <div class="callout-section-content-text">
                <h3 class="callout-section-title">
                  Join Thousands, Reach Millions
                </h3>
                <p class="callout-section-text">
                  Be a theological lifeline for Christians through your monthly
                  gift.
                </p>
              </div>
              <a
                href="${safeDonateUrl}"
                class="btn"
                @click=${handleDonateClick("monthly")}
                >Give Monthly</a
              >
            </div>
          </section>
          <section class="callout-section">
            <img
              class="callout-section-icon"
              src="//images.ctfassets.net/ukqj0ybhazqg/4J4T7D8n2HQkmJb5hxwDoa/ffea6f71d9f8f2dcc203fea7bb0d2e2d/Globe_1.svg"
              alt=""
            />
            <div class="callout-section-content">
              <div class="callout-section-content-text">
                <h3 class="callout-section-title">
                  Support Ligonier’s Global Mission
                </h3>
                <p class="callout-section-text">
                  Your generosity fuels gospel outreach around the world.
                </p>
              </div>
              <a
                href="${safeDonateUrl}"
                class="btn"
                @click=${handleDonateClick("once")}
                >One-time Gift</a
              >
            </div>
          </section>
        </div>
      </div>
    </div>
  `;
}
